import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import {
  CalendarMonth,
  AccessTime,
  Person,
  LocationOn,
  LocationCity,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import axiosInstance from "helpers/axiosInstance";
import axios from "axios";
import { LocalStorage } from "enums/localStorage";
import { useAlert } from "contexts/alertContext";
import { BASE_URL } from "constants/api";
import { handleError } from "helpers/utils";

interface WorkshopEnrollmentDetails {
  workshopId: string;
  topicName: string;
  topicNameAr: string;
  trainerName: string;
  trainerNameAr: string;
  venueName: string;
  venueNameAr: string;
  cityName: string;
  cityNameAr: string;
  vendorId: string;
  vendorName: string;
  vendorNameAr: string;
  vendorLogo: string;
  fromDate: string;
  toDate: string;
  fromDateDisplay: string;
  toDateDisplay: string;
  fromTime: string;
  toTime: string;
  fromTimeDisplay: string;
  toTimeDisplay: string;
  status: number;
  capacity: number;
  enrolledCount: number;
  link: string;
  modality: number;
  requiresInvitation: boolean;
  learningOutcome: string;
  learningOutcomeAr: string;
  prerequisite: string;
}

interface EnrollmentStatus {
  isEnrolled: boolean;
  enrollmentStatus: number;
  employeeId: string;
}

const WorkshopEnrollment: React.FC = () => {
  const { workshopId } = useParams<{ workshopId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [workshop, setWorkshop] = useState<WorkshopEnrollmentDetails | null>(
    null
  );
  const [enrollmentStatus, setEnrollmentStatus] =
    useState<EnrollmentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAuthOptions, setShowAuthOptions] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [fetchingCompany, setFetchingCompany] = useState(false);

  const isAuthenticated = () => {
    return !!localStorage.getItem(LocalStorage.ACCESS_TOKEN);
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      // Store the workshop ID in localStorage to redirect after login/registration
      localStorage.setItem("enrollmentRedirect", workshopId || "");
      setShowAuthOptions(true);
      fetchCompanyId();
      return;
    }

    fetchWorkshopDetails();
    checkEnrollmentStatus();
  }, [workshopId]);

  const fetchCompanyId = async () => {
    setFetchingCompany(true);
    try {
      // Use base axios (not axiosInstance) since this is an AllowAnonymous endpoint
      const workshopResponse = await axios.get(
        `${BASE_URL}/api/workshop/enrollment/${workshopId}`
      );
      setCompanyId(workshopResponse.data.companyId);
      setLoading(false);
    } catch (err: any) {
      setError(handleError(err) || "Error fetching workshop details");
      setLoading(false);
    } finally {
      setFetchingCompany(false);
    }
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleRegister = () => {
    if (companyId) {
      navigate(`/register/${companyId}`);
    } else {
      setAlert({
        open: true,
        message: t("T_registration_not_available") || "Registration is not available for this workshop",
        type: "error",
      });
    }
  };

  const fetchWorkshopDetails = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/workshop/enrollment/${workshopId}`
      );
      setWorkshop(response.data);
      setLoading(false);
    } catch (err: any) {
      setError(
        handleError(err) ||
          t("T_error_fetching_workshop") ||
          "Error fetching workshop details"
      );
      setLoading(false);
    }
  };

  const checkEnrollmentStatus = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/enrollment/check-enrollment/${workshopId}`
      );
      setEnrollmentStatus(response.data);
    } catch (err: any) {
      console.error("Error checking enrollment status:", err);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const response = await axiosInstance.post(
        "/api/enrollment/enroll-via-link",
        {
          workshopId: workshopId,
          status: 0,
        }
      );

      setAlert({
        open: true,
        message:
          t("T_enrollment_success") || "Successfully enrolled in workshop",
        type: "success",
      });

      // Redirect to workshop details page after successful enrollment
      setTimeout(() => {
        navigate(`/workshops/${workshopId}`);
      }, 2000);
    } catch (err: any) {
      // Handle 400 bad request and other errors
      const errorMessage = 
        handleError(err) ||
        t("T_enrollment_error") ||
        "Error enrolling in workshop";
      
      setAlert({
        open: true,
        message: errorMessage,
        type: "error",
      });
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Auth options for unauthenticated users - check this BEFORE workshop error
  if (showAuthOptions) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h4" gutterBottom fontWeight="bold" align="center">
              {t("T_workshop_enrollment") || "Workshop Enrollment"}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1" sx={{ mb: 4 }} align="center">
              {t("T_login_or_register_to_enroll") || "Please login or register to enroll in this workshop"}
            </Typography>
            
            <Stack spacing={2}>
              <Button
                onClick={handleLogin}
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={fetchingCompany}
              >
                {t("T_already_have_account") || "Already have an account? Login"}
              </Button>
              
              <Button
                onClick={handleRegister}
                variant="outlined"
                color="primary"
                size="large"
                fullWidth
                disabled={fetchingCompany || !companyId}
              >
                {fetchingCompany
                  ? t("T_loading") || "Loading..."
                  : t("T_register_new_account") || "Register New Account"}
              </Button>
            </Stack>

            {!companyId && !fetchingCompany && (
              <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                {t("T_registration_not_available") || "Registration is not available for this workshop"}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Workshop error check - only for authenticated users
  if (error || !workshop) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Alert severity="error">
            {error || t("T_workshop_not_found") || "Workshop not found"}
          </Alert>
        </Box>
      </Container>
    );
  }

  const isArabic = i18n.language === "ar";

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t("T_workshop_enrollment") || "Workshop Enrollment"}
          </Typography>
          <Divider sx={{ my: 2 }} />

          {enrollmentStatus?.isEnrolled ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t("T_already_enrolled") ||
                "You are already enrolled in this workshop"}
            </Alert>
          ) : null}

          <Grid container spacing={3}>
            {/* Workshop Topic */}
            <Grid item xs={12}>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {isArabic ? workshop.topicNameAr : workshop.topicName}
              </Typography>
            </Grid>

            {/* Vendor Name and Logo */}
            {workshop.vendorName && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  {workshop.vendorLogo && (
                    <Box
                      component="img"
                      src={`${BASE_URL}/uploads/${workshop.vendorLogo}`}
                      alt={isArabic ? workshop.vendorNameAr : workshop.vendorName}
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: "contain",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                        padding: 1,
                      }}
                      onError={(e: any) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {t("T_provider") || "Provider"}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {isArabic ? workshop.vendorNameAr : workshop.vendorName}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ mt: 2 }} />
              </Grid>
            )}

            {/* Date */}
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonth color="action" />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t("T_from_date") || "From"}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {workshop.fromDateDisplay}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonth color="action" />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t("T_to_date") || "To"}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {workshop.toDateDisplay}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Time */}
            {workshop.fromTimeDisplay && workshop.toTimeDisplay && (
              <>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("T_from_time") || "Start Time"}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {workshop.fromTimeDisplay}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("T_to_time") || "End Time"}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {workshop.toTimeDisplay}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </>
            )}

            {/* Trainer */}
            {workshop.trainerName && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Person color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {t("T_trainer") || "Trainer"}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {isArabic ? workshop.trainerNameAr : workshop.trainerName}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            )}

            {/* Venue */}
            {workshop.venueName && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationOn color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {t("T_venue") || "Venue"}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {isArabic ? workshop.venueNameAr : workshop.venueName}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            )}

            {/* City */}
            {workshop.cityName && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationCity color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {t("T_city") || "City"}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {isArabic ? workshop.cityNameAr : workshop.cityName}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            )}

            {/* Learning Outcome */}
            {workshop.learningOutcome && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {t("T_learning_outcome") || "Learning Outcome"}
                </Typography>
                <Typography variant="body1">
                  {isArabic
                    ? workshop.learningOutcomeAr
                    : workshop.learningOutcome}
                </Typography>
              </Grid>
            )}

            {/* Prerequisite */}
            {workshop.prerequisite && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {t("T_prerequisite") || "Prerequisites"}
                </Typography>
                <Typography variant="body1">{workshop.prerequisite}</Typography>
              </Grid>
            )}

            {/* Capacity Info */}
            <Grid item xs={12}>
              <Alert severity="info">
                {t("T_available_seats") || "Available Seats"}:{" "}
                {workshop.capacity - workshop.enrolledCount} / {workshop.capacity}
              </Alert>
            </Grid>

            {/* Enroll Button */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="center">
                {!enrollmentStatus?.isEnrolled ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleEnroll}
                    disabled={
                      enrolling ||
                      workshop.enrolledCount >= workshop.capacity
                    }
                    fullWidth
                  >
                    {enrolling
                      ? t("T_enrolling") || "Enrolling..."
                      : t("T_enroll_now") || "Enroll Now"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => navigate(`/workshops/${workshopId}`)}
                    fullWidth
                  >
                    {t("T_go_to_workshop") || "Go to Workshop"}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default WorkshopEnrollment;

